import { useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";
import { forecastContextSelectorQueryKeys } from "./queryKeys";

/** Options for {@link useForecastBaseContextOptions}. */
interface UseForecastBaseContextOptionsOptions {
  /**
   * User cube IDs whose values widen the master-data catalogue, so
   * values that exist only in a user cube (e.g. a merged/renamed
   * dimension value) are selectable.
   */
  cubeIds?: string[];
  /** Whether the query should run. */
  enabled?: boolean;
}

/**
 * A base hook to fetch forecast context options from the API.
 *
 * Note: staleTime is set to Infinity to avoid refetching; cube mutations
 * reset the whole `forecastContextSelectorQueryKeys.all` family.
 */
export function useForecastBaseContextOptions({
  cubeIds = [],
  enabled = true
}: UseForecastBaseContextOptionsOptions = {}) {
  const client = useApi();

  return useQuery({
    queryKey: forecastContextSelectorQueryKeys.base(cubeIds),
    meta: {
      errorMessage: "Failed to fetch forecast context options"
    },
    queryFn: async () => {
      const response = await client.POST(
        "/v2/forecast/metadata/context-options",
        {
          body: {
            fields: [],
            cube_ids: cubeIds,
            filters: {}
          }
        }
      );

      return response.data?.result ?? {};
    },
    staleTime: Infinity,
    enabled
  });
}
